import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../context/OnboardingContext';
import PageLayout from '../components/PageLayout';
import { validateBirthDate } from '../utils/signupValidation';
import './OnboardingBirthDate.css';

// `birthDate` est stockée en JJ/MM/AAAA (format d'affichage de l'onboarding).
const parseBirthDate = (value) => {
  const [day, month, year] = String(value || '').split('/').map(Number);
  return day && month && year ? new Date(year, month - 1, day) : null;
};

const DAYS_OF_WEEK = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - 1940 + 1 },
  (_, i) => CURRENT_YEAR - i
);

const OnboardingBirthDate = () => {
  const navigate = useNavigate();
  const { formData, updateField } = useOnboarding();
  const [showCalendar, setShowCalendar] = useState(false);
  const [viewDate, setViewDate] = useState(new Date(CURRENT_YEAR - 20, 0, 1));
  const [error, setError] = useState('');
  const calendarRef = useRef(null);

  const handleNext = () => {
    const birthError = validateBirthDate(parseBirthDate(formData.birthDate));
    if (birthError) {
      setError(birthError);
      return;
    }
    navigate('/onboarding/taille');
  };

  useEffect(() => {
    if (!showCalendar) return;
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendar]);

  const daysInMonth = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const offset = firstDay === 0 ? 6 : firstDay - 1;
    return { totalDays, offset };
  }, [viewDate]);

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleSelectDay = useCallback((day) => {
    const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const formatted = date.toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
    updateField('birthDate', formatted);
    // Signalé dès le choix plutôt qu'au « Continuer » : le calendrier s'ouvre sur
    // l'année en cours, un clic trop rapide y choisit une date d'aujourd'hui.
    setError(validateBirthDate(date));
    setShowCalendar(false);
  }, [viewDate, updateField]);

  const handleYearChange = (e) => {
    setViewDate(new Date(Number(e.target.value), viewDate.getMonth(), 1));
  };

  return (
    <PageLayout step={1}>
      <h2 className="title">Quelle est votre date de naissance ?</h2>
      <p className="subtitle">Pour donner des conseils adaptés à votre âge</p>

      <div className="form-group" ref={calendarRef}>
        <div className="date-input-wrapper" onClick={() => setShowCalendar(!showCalendar)}>
          <input
            type="text"
            placeholder="Sélectionner une date"
            value={formData.birthDate}
            readOnly
            className="form-input"
          />
          <div className="date-icon">📅</div>
        </div>

        {showCalendar && (
          <div className="calendar">
            <div className="calendar__header">
              <button className="calendar__nav" onClick={(e) => { e.stopPropagation(); handlePrevMonth(); }}>‹</button>
              <span className="calendar__month">
                {MONTHS[viewDate.getMonth()]}
                <select
                  className="calendar__year-select"
                  value={viewDate.getFullYear()}
                  onChange={(e) => { e.stopPropagation(); handleYearChange(e); }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {YEAR_OPTIONS.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </span>
              <button className="calendar__nav" onClick={(e) => { e.stopPropagation(); handleNextMonth(); }}>›</button>
            </div>
            <div className="calendar__weekdays">
              {DAYS_OF_WEEK.map((d) => (
                <div key={d} className="calendar__weekday">{d}</div>
              ))}
            </div>
            <div className="calendar__grid">
              {Array.from({ length: daysInMonth.offset }, (_, i) => (
                <div key={`empty-${i}`} className="calendar__day calendar__day--empty" />
              ))}
              {Array.from({ length: daysInMonth.totalDays }, (_, i) => {
                const day = i + 1;
                const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
                const formatted = date.toLocaleDateString('fr-FR', {
                  day: '2-digit', month: '2-digit', year: 'numeric'
                });
                const isSelected = formData.birthDate === formatted;
                return (
                  <div
                    key={day}
                    className={`calendar__day${isSelected ? ' calendar__day--selected' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleSelectDay(day); }}
                  >
                    {day}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {error && <p className="signup-error">{error}</p>}

      <div className="button-group">
        <button className="btn btn--primary" onClick={handleNext}>Continuer &gt;</button>
        <button className="btn btn--outline" onClick={() => navigate('/onboarding/prenom')}>&lt; Retour</button>
      </div>
    </PageLayout>
  );
};

export default OnboardingBirthDate;
